import { useState, useCallback, useMemo } from 'react';
import { CollectionType } from '../components/inventory/CollectionSelector';
import { ModeType } from '../components/inventory/ModeSelector';

export interface FilterState {
  category?: string; // Legacy single category filter
  categoryIds?: number[]; // New multi-category filter
  area?: string;
  location?: string;
  search?: string;
  // Stock-specific
  lowStock?: boolean;
  outOfStock?: boolean;
  needsReorder?: boolean;
  // Sales-specific
  availableOnly?: boolean;
  soldItems?: boolean;
  priceMin?: number;
  priceMax?: number;
}

export interface InventoryState {
  collection: CollectionType;
  mode: ModeType;
  displayStyle: 'card' | 'list' | 'compact';
  filters: FilterState;
  selectedItems: number[];
}

export const useInventoryState = () => {
  const [state, setState] = useState<InventoryState>(() => {
    console.log('[InventoryState] Initializing...');
    
    // Get current user ID from auth token
    const authToken = localStorage.getItem('auth_token');
    let currentUserId: string | null = null;
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        // Convert to string for consistent comparison with localStorage
        currentUserId = String(payload.user_id);
        console.log('[InventoryState] Current user ID:', currentUserId);
      } catch (e) {
        console.log('[InventoryState] No valid auth token');
      }
    }

    // Check if this is a different user than last time
    const lastUserId = localStorage.getItem('inventory-last-user-id');
    console.log('[InventoryState] Last user ID:', lastUserId);
    
    if (currentUserId && lastUserId && currentUserId !== lastUserId) {
      // Different user logged in - clear previous user's preferences
      console.log('[InventoryState] Different user detected, clearing previous preferences');
      localStorage.removeItem('inventory-collection');
      localStorage.removeItem('inventory-last-user-id');
    }

    // Store current user ID for next time (already a string)
    if (currentUserId) {
      localStorage.setItem('inventory-last-user-id', currentUserId);
    }

    // Restore last viewed collection from localStorage
    const savedCollection = localStorage.getItem('inventory-collection');
    let initialCollection: CollectionType = (savedCollection as CollectionType) || 'all';
    
    return {
      collection: initialCollection,
      mode: 'view',
      displayStyle: 'card',
      filters: {},
      selectedItems: []
    };
  });

  const setCollection = useCallback((collection: CollectionType) => {
    localStorage.setItem('inventory-collection', collection);
    setState(prev => ({
      ...prev,
      collection,
      selectedItems: [], // Clear selection on collection change
      filters: {} // Clear filters when changing collection
    }));
  }, []);

  const setMode = useCallback((mode: ModeType) => {
    setState(prev => ({
      ...prev,
      mode,
      selectedItems: [] // Clear selection on mode change
    }));
  }, []);

  const setDisplayStyle = useCallback((displayStyle: 'card' | 'list' | 'compact') => {
    setState(prev => ({ ...prev, displayStyle }));
  }, []);

  const setFilters = useCallback((filters: FilterState) => {
    setState(prev => ({ ...prev, filters }));
  }, []);

  const toggleItemSelection = useCallback((itemId: number) => {
    setState(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(itemId)
        ? prev.selectedItems.filter(id => id !== itemId)
        : [...prev.selectedItems, itemId]
    }));
  }, []);

  const selectAll = useCallback((itemIds: number[]) => {
    setState(prev => ({ ...prev, selectedItems: itemIds }));
  }, []);

  const clearSelection = useCallback(() => {
    setState(prev => ({ ...prev, selectedItems: [] }));
  }, []);

  return {
    state,
    setCollection,
    setMode,
    setDisplayStyle,
    setFilters,
    toggleItemSelection,
    selectAll,
    clearSelection
  };
};

// Hook to calculate item counts per collection
export const useItemCounts = (items: any[]) => {
  return useMemo(() => ({
    all: items.length,
    stock: items.filter(i => i.stock_keeping).length,
    sales: items.filter(i => i.for_sale).length
  }), [items]);
};
